import { TbEye } from "react-icons/tb";
import intl from "react-intl-universal";

import { CurrencyIconPair, CurrencyIconSlot } from "../../../../Components/Atoms/CurrencyIcon";

import {
  formatCrossborderAmount,
  getTransactionOperationFee,
  MOCK_HISTORY,
} from "../constants";

import { CrossborderStatusBadge } from "./CrossborderStatusBadge";
import { PaymentRouteChips } from "./PaymentRouteChips";

const GRID_COLS =
  "lg:grid-cols-[minmax(0,1.5fr)_minmax(0,2fr)_minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.05fr)]";

const TypeBadge = ({ type }) => (
  <span
    className={`inline-flex w-[4.5rem] shrink-0 items-center justify-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${
      type === "payin"
        ? "bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300"
        : "bg-violet-50 text-violet-700 dark:bg-violet-950/40 dark:text-violet-300"
    }`}
  >
    {type === "payin" ? intl.get("crossborder.typePayin") : intl.get("crossborder.typePayout")}
  </span>
);

const DetailButton = ({ onClick }) => (
  <button
    type="button"
    className="btn-outline-icon shrink-0"
    onClick={onClick}
    aria-label={intl.get("common.details")}
  >
    <TbEye />
  </button>
);

const HistoryHeaderRow = () => (
  <div
    className="col-span-6 grid-history-header hidden lg:-mx-4 lg:grid lg:grid-cols-subgrid lg:items-center lg:gap-x-4 lg:px-4 lg:min-h-12 lg:py-2"
  >
    <div className="flex min-w-0 items-center gap-2">
      <div className="w-10 shrink-0" aria-hidden="true" />
      <span>{intl.get("crossborder.historyColumn.operation")}</span>
    </div>
    <span className="text-center">{intl.get("crossborder.historyColumn.paymentMethod")}</span>
    <span>{intl.get("crossborder.historyColumn.recipient")}</span>
    <span className="text-right">{intl.get("crossborder.historyColumn.origin")}</span>
    <span className="text-right">{intl.get("crossborder.historyColumn.amount")}</span>
    <div className="flex items-center justify-end gap-2.5">
      <span>{intl.get("crossborder.historyColumn.status")}</span>
      <div className="invisible shrink-0" aria-hidden="true">
        <DetailButton onClick={() => {}} />
      </div>
    </div>
  </div>
);

const GridCell = ({ label, children, align = "left", className = "" }) => (
  <div className={className}>
    <p
      className={`mb-px text-xs font-semibold uppercase tracking-wide text-gray-400 lg:sr-only ${
        align === "right" ? "text-right" : align === "center" ? "text-center" : ""
      }`}
    >
      {label}
    </p>
    <div
      className={
        align === "right" ? "text-right" : align === "center" ? "flex justify-center" : ""
      }
    >
      {children}
    </div>
  </div>
);

const TypeSummaryCell = ({ item }) => (
  <div className="flex min-w-0 items-start gap-2.5 lg:items-center lg:gap-2">
    <CurrencyIconSlot>
      <CurrencyIconPair from={item.fromCurrency} to={item.toCurrency} />
    </CurrencyIconSlot>
    <div className="min-w-0">
      <TypeBadge type={item.type} />
      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{item.time}</p>
    </div>
  </div>
);

const HistoryRow = ({ item, onShowDetail }) => {
  const originAmount = formatCrossborderAmount(item.fromAmount, item.fromCurrency);
  const destinationAmount = formatCrossborderAmount(item.toAmount, item.toCurrency);
  const { amount: feeAmount, currencyId: feeCurrency } = getTransactionOperationFee(item);
  const operationFee = formatCrossborderAmount(feeAmount, feeCurrency);

  const handleOpenDetail = () => onShowDetail(item);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleOpenDetail}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          handleOpenDetail();
        }
      }}
      className={`flex cursor-pointer flex-col gap-2 border-b border-gray-100 px-4 py-2.5 transition-colors last:border-b-0 hover:bg-gray-50/80 dark:border-brand-dark-border dark:hover:bg-white/5 lg:col-span-6 lg:grid lg:grid-cols-subgrid lg:items-center lg:gap-x-4 lg:px-0 lg:py-2`}
    >
      <GridCell label={intl.get("crossborder.historyColumn.operation")}>
        <TypeSummaryCell item={item} />
      </GridCell>

      <GridCell label={intl.get("crossborder.historyColumn.paymentMethod")} align="center">
        <PaymentRouteChips
          originRailId={item.originRailId}
          destinationRailId={item.destinationRailId}
          fromCurrency={item.fromCurrency}
          toCurrency={item.toCurrency}
          className="text-brand-dark dark:text-gray-100"
        />
      </GridCell>

      <GridCell label={intl.get("crossborder.historyColumn.recipient")}>
        <p className="truncate text-sm font-medium text-brand-dark dark:text-gray-100" title={item.senderCustomer}>
          {item.senderCustomer}
        </p>
      </GridCell>

      <GridCell label={intl.get("crossborder.historyColumn.origin")} align="right">
        <p className="text-sm font-semibold text-brand-dark dark:text-gray-100">{originAmount}</p>
      </GridCell>

      <GridCell label={intl.get("crossborder.historyColumn.amount")} align="right">
        <p className="text-sm font-semibold text-brand-dark dark:text-gray-100">{destinationAmount}</p>
        <p className="text-[11px] text-gray-400">
          {intl.get("crossborder.detail.fee")} {operationFee}
        </p>
      </GridCell>

      <div
        className="flex flex-wrap items-center gap-2.5 lg:justify-end"
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
      >
        <p className="mb-px w-full text-xs font-semibold uppercase tracking-wide text-gray-400 lg:sr-only">
          {intl.get("crossborder.historyColumn.status")}
        </p>
        <CrossborderStatusBadge status={item.status} />
        <DetailButton onClick={handleOpenDetail} />
      </div>
    </div>
  );
};

export const CrossborderHistoryTable = ({ items = MOCK_HISTORY, onShowDetail }) => {
  if (items.length === 0) {
    return (
      <div className="app-card overflow-hidden">
        <div className="app-empty-state m-4">{intl.get("crossborder.historyEmpty")}</div>
      </div>
    );
  }

  return (
    <div className="app-card overflow-hidden">
      <div className={`lg:grid ${GRID_COLS} lg:gap-x-4 lg:px-4`}>
        <HistoryHeaderRow />
        {items.map((item) => (
          <HistoryRow key={item.id} item={item} onShowDetail={onShowDetail} />
        ))}
      </div>
    </div>
  );
};
